import {ExclamationIcon} from '@heroicons/react/outline';
import {isEmpty} from 'lodash-es';
import React, {useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import Button from '@/components/common/Button';
import {Select} from '@/components/common/Input';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import {getAdminRoleOptions} from '@/constants/formatters';
import {userService} from '@/services/userService';
import {AfriexRoles} from '@/types';
import {getResponseMessage} from '@/utils/errorMessage';

import Modal from '.';
import ConfirmationModal from './ConfirmationModal';

const UpdateRoleModal: React.FC<React.PropsWithChildren<UserModalProps>> = ({
  isOpen,
  onClose,
  user,
  defaultRole,
}) => {
  const afriexUser = !isEmpty(user) ? user : null;
  const userName = afriexUser?.name.fullName;
  const [afriexRole, setAfriexRole] = useState<AfriexRoles>(
    defaultRole || AfriexRoles.USER,
  );
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const adminRoleOptions = useMemo(
    () =>
      getAdminRoleOptions([AfriexRoles.USER, AfriexRoles.ENGINEERING_MANAGER]),
    [],
  );

  const onDismiss = () => {
    onClose?.();
    setIsConfirmModalOpen(false);
  };

  const openConfirmModal = () => {
    setIsConfirmModalOpen(!isConfirmModalOpen);
  };

  const updateRole = async () => {
    setLoading(!loading);
    try {
      await userService.updateUserRole(user?.id, afriexRole);
      toast.success(`Successfully updated role for ${userName}`);
      onDismiss();
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      toast.error(
        getResponseMessage(error) || `Failed to update role for ${userName}`,
      );
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width="w-2/6"
        closeOnOutsideClick
        title={`Update role for ${userName}`}
      >
        <p className="flex gap-2 justify-center">
          <ExclamationIcon className="w-6 h-6 text-failed-500" /> You are about
          to update role for {userName}.
        </p>

        <div className="mt-6">
          <Select
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setAfriexRole(e.target.value as AfriexRoles)
            }
            value={afriexRole}
            name="role"
            options={adminRoleOptions}
            placeholder="Select Role"
          />
        </div>

        <div className="flex justify-end mt-6">
          <Button
            colorScheme="red"
            onClick={openConfirmModal}
            className="h-10"
            disabled={afriexRole.length === 0}
          >
            Continue
          </Button>
        </div>
      </Modal>

      {!isEmpty(user) && (
        <ConfirmationModal
          isOpen={isConfirmModalOpen}
          label={`Are you sure you want to update role for ${userName}`}
          cancelAction={onDismiss}
          cancelActionLabel="No"
          confirmAction={updateRole}
          confirmActionLabel="Yes"
          loadingLabel="Updating..."
          loading={loading}
          title=""
        />
      )}
    </React.Fragment>
  );
};

export default UpdateRoleModal;
